import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { adminApi, fileUrl, API } from "../../lib/api";
import axios from "axios";
import { useDialog } from "../../lib/dialog";
import { useEvents } from "../../lib/events";
import AdminLayout from "../../components/app/AdminLayout";
import { Loader } from "./Dashboard";
import { CharacterProfileBuilder } from "../../components/app/CharacterProfile";
import { ActorSearchList, ModalShell, AssignDatesModal } from "../../components/app/AssignModals";
import { SkullRatingInput } from "../../components/app/SkullRating";
import { ABILITIES } from "../../lib/scoring";
import { Plus, Trash2, Pencil, X, Upload, Loader2, ChevronDown, ChevronRight, ImageIcon, Eye, UserPlus, Check, Users, Star, ArrowUpDown, GripVertical, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";

const EMPTY = { name: "", character_name: "", department: "", placement: "", quantity: 1, reference_images: [], profile_image: "", show_on_public: true, skill_level: 0, ability_targets: {}, preferred_gender: "either", profile_fields: [], profile_values: {}, custom_fields: [] };

const groupTypes = (items) => {
  const map = new Map();
  for (const r of items) {
    const tid = r.role_type_id || r.id;
    if (!map.has(tid)) map.set(tid, { role_type_id: tid, positions: [] });
    map.get(tid).positions.push(r);
  }
  const types = [];
  for (const t of map.values()) {
    t.positions.sort((a, b) => (a.position_number || 0) - (b.position_number || 0) || (a.name || "").localeCompare(b.name || ""));
    const rep = t.positions[0];
    t.minSort = Math.min(...t.positions.map((p) => (p.builder_sort_order ?? 1e9)));
    t.base_name = rep.base_name || rep.name;
    t.placement = rep.placement;
    t.character_name = rep.character_name;
    t.department = rep.department;
    t.reference_images = rep.reference_images || [];
    t.profile_image = rep.profile_image || "";
    t.show_on_public = rep.show_on_public;
    t.skill_level = rep.skill_level || 0;
    t.ability_targets = rep.ability_targets || {};
    t.preferred_gender = rep.preferred_gender || "either";
    t.profile_fields = rep.profile_fields || [];
    t.profile_values = rep.profile_values || {};
    t.custom_fields = rep.custom_fields || [];
    t.rep = rep;
    types.push(t);
  }
  types.sort((a, b) => a.minSort - b.minSort || a.base_name.toLowerCase().localeCompare(b.base_name.toLowerCase()));
  return types;
};

export default function Roles() {
  const dialog = useDialog();
  const { isArchived, currentEvent } = useEvents();
  const [items, setItems] = useState(null);
  const [actors, setActors] = useState([]);
  const [showDates, setShowDates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRepId, setEditingRepId] = useState(null); // representative position id when editing a type
  const [editingTypeId, setEditingTypeId] = useState(null); // role_type_id whose inline edit form is open
  const [form, setForm] = useState(EMPTY);
  const [expanded, setExpanded] = useState({});
  const [renaming, setRenaming] = useState(null); // { id, value }
  const [params, setParams] = useSearchParams();
  const [assignRole, setAssignRole] = useState(null);
  const [assignActor, setAssignActor] = useState(null);
  // Update 27 Change 10B: manual role-builder sort mode
  const [sortMode, setSortMode] = useState(false);
  const [order, setOrder] = useState([]);
  const [savingSort, setSavingSort] = useState(false);
  const [dragId, setDragId] = useState(null);

  const load = () => adminApi.get("/admin/roles").then(({ data }) => setItems(data.items));
  useEffect(() => {
    load();
    adminApi.get("/admin/applicants?status=offered_role,contracted&limit=200").then(({ data }) => setActors(data.items));
    adminApi.get("/admin/show-dates").then(({ data }) => setShowDates(data.items || []));
  }, []);

  const types = useMemo(() => (items ? groupTypes(items) : []), [items]);

  // Update 27 Change 10A: position-level assignment summary.
  const stats = useMemo(() => {
    const list = items || [];
    const totalDates = showDates.length;
    let fully = 0, partial = 0, vacant = 0;
    for (const r of list) {
      const n = (r.assignments || []).length;
      if (n === 0) vacant += 1;
      else if (totalDates > 0 && n >= totalDates) fully += 1;
      else partial += 1;
    }
    return { total: list.length, fully, partial, vacant };
  }, [items, showDates]);

  const enterSort = () => { setOrder(types.map((t) => t.role_type_id)); setSortMode(true); };
  const moveType = (id, dir) => setOrder((o) => {
    const a = [...o]; const i = a.indexOf(id); const j = i + dir;
    if (j < 0 || j >= a.length) return a;
    [a[i], a[j]] = [a[j], a[i]]; return a;
  });
  const dropOnType = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    setOrder((o) => { const a = [...o]; const from = a.indexOf(dragId); const to = a.indexOf(targetId); a.splice(from, 1); a.splice(to, 0, dragId); return a; });
    setDragId(null);
  };
  const saveSort = async () => {
    setSavingSort(true);
    try {
      await adminApi.post("/admin/roles/reorder", { ordered_type_ids: order });
      setSortMode(false); load();
    } finally { setSavingSort(false); }
  };
  const typesById = Object.fromEntries(types.map((t) => [t.role_type_id, t]));
  const sortedTypes = sortMode ? order.map((id) => typesById[id]).filter(Boolean) : types;

  useEffect(() => {
    const eid = params.get("edit");
    if (eid && items) {
      const it = items.find((x) => x.id === eid);
      if (it) { openEditType(it.role_type_id || it.id); }
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, items]);

  const openCreate = () => { setEditingRepId(null); setEditingTypeId(null); setForm(EMPTY); setShowForm(true); };
  const openEditType = (tid) => {
    const t = groupTypes(items).find((x) => x.role_type_id === tid);
    if (!t) return;
    setShowForm(false);
    setEditingRepId(t.rep.id);
    setEditingTypeId(tid);
    setExpanded((e) => ({ ...e, [tid]: true }));
    setForm({
      name: t.base_name || "", character_name: t.character_name || "", department: t.department || "",
      placement: t.placement || "", quantity: t.positions.length,
      reference_images: t.reference_images || [],
      profile_image: t.profile_image || "", show_on_public: t.show_on_public !== false,
      skill_level: t.skill_level || 0,
      ability_targets: t.ability_targets || {},
      preferred_gender: t.preferred_gender || "either",
      profile_fields: t.profile_fields || [], profile_values: t.profile_values || {}, custom_fields: t.custom_fields || [],
    });
  };
  const closeForm = () => {
    const tid = editingTypeId;
    setShowForm(false); setEditingRepId(null); setEditingTypeId(null); setForm(EMPTY);
    if (tid) setExpanded((e) => ({ ...e, [tid]: false })); // collapse the row back to normal
  };

  const save = async () => {
    if (!form.name) return;
    const payload = { ...form, quantity: Math.max(1, parseInt(form.quantity, 10) || 1) };
    if (editingRepId) await adminApi.patch(`/admin/roles/${editingRepId}`, payload);
    else await adminApi.post("/admin/roles", payload);
    closeForm(); load();
  };

  const delType = async (t) => {
    const n = t.positions.length;
    if (await dialog.confirm({ title: `Delete the "${t.base_name}" role?`, description: `This removes ${n} position${n === 1 ? "" : "s"} and all their date assignments. This cannot be undone.`, confirmLabel: "Delete", destructive: true })) {
      await adminApi.delete(`/admin/role-types/${t.role_type_id}`); load();
    }
  };

  const delPosition = async (pos) => {
    const assigned = (pos.assigned_actors || []).map((a) => a.actor_name).filter(Boolean);
    const warn = assigned.length ? `${pos.name} has ${assigned.join(", ")} assigned. Deleting this position will remove their assignment. Continue?` : `Delete ${pos.name}? This cannot be undone.`;
    if (await dialog.confirm({ title: "Delete position?", description: warn, confirmLabel: "Delete", destructive: true })) {
      await adminApi.delete(`/admin/roles/${pos.id}`); load();
    }
  };

  const saveRename = async () => {
    const v = (renaming.value || "").trim();
    if (v) await adminApi.patch(`/admin/roles/${renaming.id}/rename`, { name: v });
    setRenaming(null); load();
  };

  const removeActor = async (pos, actor) => {
    if (await dialog.confirm({ title: `Remove ${actor.actor_name}?`, description: `Removes ${actor.actor_name} from ${pos.name} on all dates.`, confirmLabel: "Remove", destructive: true })) {
      await adminApi.post(`/admin/roles/${pos.id}/unassign-actor`, { actor_id: actor.actor_id }); load();
    }
  };

  const confirmAssign = async ({ date_ids, send_email }) => {
    await adminApi.post(`/admin/roles/${assignRole.id}/assign-dates`, { actor_id: assignActor.id, date_ids, send_email });
    setAssignRole(null); setAssignActor(null); load();
  };

  const toggleExpand = (tid) => setExpanded((e) => ({ ...e, [tid]: !e[tid] }));
  const pickableActors = assignRole ? actors.filter((a) => !(assignRole.assigned_actors || []).some((x) => x.actor_id === a.id)) : [];
  const takenDateIds = assignRole ? (assignRole.assignments || []).map((a) => a.show_date_id) : [];

  return (
    <AdminLayout title="Role Builder" subtitle="Define role types, generate numbered positions, and assign actors per open date"
      actions={<div className="flex items-center gap-2">
        {currentEvent?.slug && (
          <a href={`/${currentEvent.slug}/roles`} target="_blank" rel="noopener noreferrer" data-testid="roles-public-link"
            className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood">
            <ExternalLink size={14} /> Public Characters
          </a>
        )}
        {!isArchived && (<>
          {sortMode ? (
            <button onClick={saveSort} disabled={savingSort} data-testid="role-sort-done" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-[#16A34A] text-white hover:bg-[#15803D] disabled:opacity-50">
              {savingSort ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Done
            </button>
          ) : (
            <button onClick={enterSort} data-testid="role-sort-toggle" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-blood hover:text-blood"><ArrowUpDown size={14} /> Sort</button>
          )}
          <button onClick={() => (showForm ? closeForm() : openCreate())} data-testid="new-role-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]"><Plus size={14} /> New Role</button>
        </>)}
      </div>}>
      {items && items.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 px-4 py-3 border border-[#1a1a1a] bg-[#0A0A0A] text-sm" data-testid="roles-status-bar">
          <span className="font-display uppercase tracking-tight text-[#F2EBE1]">{stats.total} role{stats.total === 1 ? "" : "s"} total</span>
          <span className="flex items-center gap-1.5 text-[#10B981]"><span className="w-2 h-2 rounded-full bg-[#10B981]" /> {stats.fully} fully assigned</span>
          <span className="flex items-center gap-1.5 text-[#F59E0B]"><span className="w-2 h-2 rounded-full bg-[#F59E0B]" /> {stats.partial} partial</span>
          <span className="flex items-center gap-1.5 text-blood"><span className="w-2 h-2 rounded-full bg-blood" /> {stats.vacant} vacant</span>
        </div>
      )}
      {sortMode && <div className="mb-4 px-3 py-2 text-xs text-[#F59E0B] border border-[#F59E0B]/40 bg-[#F59E0B]/10" data-testid="role-sort-banner">Sort mode — drag the handle or use the arrows to reorder roles, then click Done. This is the Role Builder order (the roster can optionally follow it).</div>}
      {showForm && !editingTypeId && (
        <div className="mb-4 border border-[#262626] bg-[#0A0A0A]" data-testid="role-create-form">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a]">
            <h3 className="font-display text-lg uppercase tracking-tight">Add Role</h3>
            <button onClick={closeForm} data-testid="role-form-close" className="text-[#6B6862] hover:text-blood"><X size={18} /></button>
          </div>
          <div className="p-4">
            <RoleForm form={form} setForm={setForm} editingRepId={editingRepId} onSave={save} onCancel={closeForm} />
          </div>
        </div>
      )}

      {!items ? <Loader /> : sortMode ? (
        <div className="space-y-2" data-testid="role-sort-list">
          {sortedTypes.map((t) => (
            <div key={t.role_type_id} data-testid={`role-sort-row-${t.role_type_id}`}
              draggable onDragStart={() => setDragId(t.role_type_id)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropOnType(t.role_type_id)}
              className="flex items-center gap-3 border border-[#1a1a1a] bg-[#0A0A0A] px-4 py-3">
              <GripVertical size={16} className="text-[#6B6862] cursor-grab shrink-0" />
              <div className="flex flex-col shrink-0">
                <button onClick={() => moveType(t.role_type_id, -1)} data-testid={`role-move-up-${t.role_type_id}`} className="text-[#6B6862] hover:text-[#F2EBE1] leading-none"><ArrowUp size={14} /></button>
                <button onClick={() => moveType(t.role_type_id, 1)} data-testid={`role-move-down-${t.role_type_id}`} className="text-[#6B6862] hover:text-[#F2EBE1] leading-none"><ArrowDown size={14} /></button>
              </div>
              {t.profile_image ? (
                <img src={fileUrl(t.profile_image)} alt={t.base_name} className="w-9 h-9 shrink-0 object-cover border border-[#262626]" />
              ) : (
                <div className="w-9 h-9 shrink-0 border border-[#1a1a1a] bg-[#050505] flex items-center justify-center text-[#3a3835]"><ImageIcon size={14} /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display uppercase tracking-tight truncate">{t.base_name}</div>
                <div className="text-[10px] font-mono text-[#6B6862]">{t.positions.length} position{t.positions.length === 1 ? "" : "s"}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {types.length === 0 && <div className="border border-[#1a1a1a] bg-[#0A0A0A] py-12 text-center text-[#6B6862]">No roles defined yet.</div>}
          {types.map((t) => (
            <div key={t.role_type_id} className="border border-[#1a1a1a] bg-[#0A0A0A]" data-testid={`role-type-${t.role_type_id}`}>
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1a1a1a]">
                <button onClick={() => toggleExpand(t.role_type_id)} data-testid={`role-type-expand-${t.role_type_id}`} className="text-[#6B6862] hover:text-[#F2EBE1]">
                  {expanded[t.role_type_id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {t.profile_image ? (
                  <img src={fileUrl(t.profile_image)} alt={t.base_name} data-testid={`role-type-profile-${t.role_type_id}`} className="w-10 h-10 shrink-0 object-cover border border-[#262626]" />
                ) : (
                  <div className="w-10 h-10 shrink-0 border border-[#1a1a1a] bg-[#050505] flex items-center justify-center text-[#3a3835]"><ImageIcon size={16} /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display uppercase tracking-tight text-lg leading-none flex items-center gap-2">
                    {t.base_name}
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[#6B6862] normal-case tracking-normal"><Users size={11} />{t.positions.length}</span>
                    {(t.reference_images || []).length > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[#6B6862] normal-case tracking-normal"><ImageIcon size={11} />{t.reference_images.length}</span>}
                  </div>
                  {t.placement && <div className="text-[11px] text-[#6B6862] mt-1 truncate">{t.placement}</div>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Link to={`/admin/roles/${t.rep.id}`} data-testid={`role-view-${t.role_type_id}`} className="text-[#6B6862] hover:text-[#F2EBE1]" title="View character sheet"><Eye size={15} /></Link>
                  {!isArchived && <>
                    <button onClick={() => openEditType(t.role_type_id)} data-testid={`role-edit-${t.role_type_id}`} className="text-[#6B6862] hover:text-[#F2EBE1]" title="Edit role type"><Pencil size={15} /></button>
                    <button onClick={() => delType(t)} data-testid={`role-delete-${t.role_type_id}`} className="text-[#6B6862] hover:text-blood" title="Delete role type"><Trash2 size={15} /></button>
                  </>}
                </div>
              </div>

              {editingTypeId === t.role_type_id && !isArchived && (
                <div className="border-b border-[#1a1a1a] bg-[#070707] p-4" data-testid={`role-edit-form-${t.role_type_id}`}>
                  <h4 className="font-display text-sm uppercase tracking-wider text-blood mb-3">Edit Role Type</h4>
                  <RoleForm form={form} setForm={setForm} editingRepId={editingRepId} onSave={save} onCancel={closeForm} />
                </div>
              )}

              {expanded[t.role_type_id] && (
                <div className="divide-y divide-[#141414]">
                  {t.positions.map((pos) => (
                    <div key={pos.id} className="px-4 py-3 flex items-start gap-3" data-testid={`role-position-${pos.id}`}>
                      <div className="w-48 shrink-0 pt-0.5">
                        {renaming?.id === pos.id ? (
                          <div className="flex items-center gap-1">
                            <input autoFocus value={renaming.value} onChange={(e) => setRenaming({ id: pos.id, value: e.target.value })}
                              onKeyDown={(e) => e.key === "Enter" && saveRename()} data-testid={`role-rename-input-${pos.id}`}
                              className="w-full bg-[#050505] border border-blood px-2 py-1 text-xs focus:outline-none" />
                            <button onClick={saveRename} data-testid={`role-rename-save-${pos.id}`} className="text-[#10B981] p-1"><Check size={14} /></button>
                            <button onClick={() => setRenaming(null)} className="text-[#6B6862] p-1"><X size={14} /></button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 group">
                            <span className="font-display uppercase text-sm tracking-tight">{pos.name}</span>
                            {!isArchived && <button onClick={() => setRenaming({ id: pos.id, value: pos.name })} data-testid={`role-rename-${pos.id}`} className="text-[#6B6862] hover:text-[#F2EBE1] opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={11} /></button>}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid={`role-assigned-${pos.id}`}>
                          {(pos.assigned_actors || []).length === 0 && <span className="text-xs text-[#6B6862] italic">No actors assigned.</span>}
                          {(pos.assigned_actors || []).map((a) => (
                            <span key={a.actor_id} data-testid={`role-actor-chip-${pos.id}-${a.actor_id}`} className="inline-flex items-center gap-1.5 px-2 py-1 border border-[#262626] bg-[#070707] text-xs">
                              <span>{a.actor_name}</span>
                              <span className="text-[10px] font-mono text-[#6B6862]">{a.date_count}d</span>
                              {!isArchived && <button onClick={() => removeActor(pos, a)} data-testid={`role-actor-remove-${pos.id}-${a.actor_id}`} className="text-[#6B6862] hover:text-blood"><X size={11} /></button>}
                            </span>
                          ))}
                        </div>
                        {!isArchived && (
                          <button onClick={() => { setAssignRole(pos); setAssignActor(null); }} data-testid={`role-assign-actor-${pos.id}`}
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-blood hover:text-[#FF3333]"><UserPlus size={13} /> Assign Actor</button>
                        )}
                      </div>
                      {!isArchived && (
                        <button onClick={() => delPosition(pos)} data-testid={`role-position-delete-${pos.id}`} className="text-[#6B6862] hover:text-blood shrink-0 pt-0.5" title="Delete position"><Trash2 size={14} /></button>
                      )}
                    </div>
                  ))}
                  {(t.reference_images || []).length > 0 && (
                    <div className="px-4 py-4 bg-[#070707]"><RoleReference role={t} /></div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="mt-4 text-xs text-[#6B6862]">Tip: Only applicants at status <span className="text-[#84CC16]">Offered a Role</span> or above can be assigned. Each position can have different actors on different open dates. To swap an actor on a single night or reorder the roster, use the <Link to="/admin/roster" className="text-[#F2EBE1] underline">Roster</Link>.</p>

      {assignRole && !assignActor && (
        <ModalShell testid="role-actor-picker" title={`Assign an actor`} subtitle={`to ${assignRole.name}`} onClose={() => setAssignRole(null)}>
          <ActorSearchList actors={pickableActors} onSelect={(a) => setAssignActor(a)} emptyText="All contracted actors are already assigned to this position." testidPrefix="role-pick" />
        </ModalShell>
      )}
      {assignRole && assignActor && (
        <AssignDatesModal actor={assignActor} role={assignRole} showDates={showDates} takenDateIds={takenDateIds}
          occupiedByOther={(items || []).reduce((acc, r) => {
            if (r.id === assignRole.id) return acc;
            (r.assignments || []).forEach((a) => { if (a.actor_id === assignActor.id) acc[a.show_date_id] = r.name; });
            return acc;
          }, {})}
          onConfirm={confirmAssign} onClose={() => setAssignActor(null)} />
      )}
    </AdminLayout>
  );
}

// Reusable reference image gallery (admin expand + assign-role tab)
export function RoleReference({ role, compact = false }) {
  const imgs = role.reference_images || [];
  if (imgs.length === 0) return <p className="text-xs text-[#6B6862] italic">No reference images added yet.</p>;
  return (
    <div className="space-y-3">
      <div>
        <div className="label-mini mb-1.5">Reference Images</div>
        <div className={`grid gap-2 ${compact ? "grid-cols-4" : "grid-cols-6"}`}>
          {imgs.map((id) => (
            <a key={id} href={fileUrl(id)} target="_blank" rel="noopener noreferrer" data-testid={`role-ref-img-${id}`}>
              <img src={fileUrl(id)} alt="reference" className="w-full aspect-square object-cover border border-[#262626] hover:border-blood transition-colors" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// Multi-image uploader using the public upload endpoint
export function ImageUploader({ images, onAdd, onRemove, max = 10, profileImage, onSetProfile }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();
  const upload = async (files) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (images.length >= max) break;
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/public/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onAdd(data.file_id);
      }
    } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div>
      <div className="grid grid-cols-5 gap-2 mb-2">
        {images.map((id) => {
          const isProfile = profileImage === id;
          return (
            <div key={id} className="relative group">
              <img src={fileUrl(id)} alt="reference" className={`w-full aspect-square object-cover border ${isProfile ? "border-blood ring-1 ring-blood" : "border-[#262626]"}`} />
              {onSetProfile && (
                <button type="button" onClick={() => onSetProfile(isProfile ? "" : id)} data-testid={`set-profile-img-${id}`}
                  className={`absolute bottom-1 left-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider flex items-center gap-0.5 transition-opacity ${isProfile ? "bg-blood text-white" : "bg-black/80 text-[#A19D94] opacity-0 group-hover:opacity-100 hover:text-[#F2EBE1]"}`}>
                  <Star size={9} className={isProfile ? "fill-current" : ""} /> {isProfile ? "Profile" : "Set"}
                </button>
              )}
              <button type="button" onClick={() => onRemove(id)} data-testid={`remove-ref-img-${id}`}
                className="absolute top-1 right-1 bg-black/80 text-[#A19D94] hover:text-blood p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
            </div>
          );
        })}
      </div>
      <label className={`flex items-center gap-3 border border-dashed border-[#3a3835] hover:border-blood p-4 cursor-pointer transition-colors ${images.length >= max ? "opacity-40 pointer-events-none" : ""}`}>
        {uploading ? <Loader2 size={18} className="animate-spin text-blood" /> : <Upload size={18} className="text-[#A19D94]" />}
        <span className="text-xs text-[#A19D94]">{uploading ? "Uploading…" : `Click to upload images (JPG, PNG, WEBP) — ${images.length}/${max}`}</span>
        <input ref={inputRef} data-testid="role-image-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e) => e.target.files?.length && upload(e.target.files)} className="hidden" />
      </label>
    </div>
  );
}

function AbilityTargetsEditor({ targets, onChange }) {
  const setVal = (key, v) => onChange({ ...targets, [key]: v });
  const activeCount = ABILITIES.filter((a) => (targets[a.key] || 0) > 0).length;
  return (
    <div data-testid="ability-targets-editor">
      <p className="text-[11px] text-[#6B6862] mb-3">{activeCount} of 12 abilities set as requirements. Higher value = more important to this role.</p>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
        {ABILITIES.map((ab) => {
          const v = targets[ab.key] || 0;
          return (
            <div key={ab.key} data-testid={`role-ability-${ab.key}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs uppercase tracking-wider ${v > 0 ? "text-[#F2EBE1]" : "text-[#6B6862]"}`}>{ab.label}</span>
                <span className={`font-mono text-sm w-6 text-right ${v > 0 ? "text-blood" : "text-[#4a4843]"}`} data-testid={`role-ability-val-${ab.key}`}>{v || "–"}</span>
              </div>
              <input type="range" min="0" max="10" step="1" value={v}
                onChange={(e) => setVal(ab.key, parseInt(e.target.value, 10))}
                data-testid={`role-ability-slider-${ab.key}`}
                className="w-full" style={{ accentColor: "#D91616" }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const I = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="label-mini block mb-1.5">{label}</label>
    <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
  </div>
);


// Shared role create/edit form body (used inline for both Add Role and per-row Edit).
function RoleForm({ form, setForm, editingRepId, onSave, onCancel }) {
  return (
    <div data-testid="role-form">
      <div className="grid md:grid-cols-2 gap-3">
        <I label="Role Name (e.g. Zombie)" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="role-name" />
        <div>
          <label className="label-mini block mb-1.5">Quantity (positions to generate)</label>
          <input data-testid="role-quantity" type="number" min={1} max={50} value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
          <p className="text-[10px] text-[#6B6862] mt-1">{editingRepId ? "Increase to add more positions (e.g. Zombie 9, 10…). Delete positions individually below." : "e.g. 8 generates Zombie 1 … Zombie 8. Leave at 1 for a single named role."}</p>
        </div>
        <I label="Placement (e.g. End of corridor, Zone B)" value={form.placement} onChange={(v) => setForm({ ...form, placement: v })} />
        <I label="Character Name (e.g. Mary the Maid)" value={form.character_name} onChange={(v) => setForm({ ...form, character_name: v })} testid="role-character-name" />
        <I label="Department / Faction (e.g. Clowns)" value={form.department} onChange={(v) => setForm({ ...form, department: v })} testid="role-department" />
        <SkullRatingInput value={form.skill_level || 0} onChange={(v) => setForm({ ...form, skill_level: v })} />
        <div className="md:col-span-2">
          <label className="label-mini block mb-1.5">Reference &amp; Profile Images <span className="text-[#6B6862] normal-case">(shared by all positions of this type — set one as the profile image)</span></label>
          <ImageUploader images={form.reference_images || []}
            profileImage={form.profile_image || ""}
            onSetProfile={(id) => setForm((f) => ({ ...f, profile_image: id }))}
            onAdd={(id) => setForm((f) => ({ ...f, reference_images: [...(f.reference_images || []), id] }))}
            onRemove={(id) => setForm((f) => ({ ...f, reference_images: (f.reference_images || []).filter((x) => x !== id), profile_image: f.profile_image === id ? "" : f.profile_image }))} />
        </div>
        <div className="md:col-span-2 flex items-start gap-2 -mt-1">
          <input type="checkbox" id="role-show-public" data-testid="role-show-public" checked={form.show_on_public !== false}
            onChange={(e) => setForm({ ...form, show_on_public: e.target.checked })}
            className="mt-0.5 accent-[#D91616]" />
          <label htmlFor="role-show-public" className="text-sm text-[#A19D94] leading-tight">Show on public roles / characters page <span className="text-[#6B6862]">(requires a profile image)</span></label>
        </div>
        <div className="md:col-span-2 pt-2 border-t border-[#1a1a1a] mt-2">
          <label className="label-mini block mb-1">Preferred Gender <span className="text-[#6B6862] normal-case">(only affects match suggestions — a "Female" role won't be suggested to a male applicant)</span></label>
          <div className="flex gap-2" data-testid="role-gender-select">
            {[["either", "Either"], ["male", "Male"], ["female", "Female"]].map(([val, lbl]) => (
              <button key={val} type="button" onClick={() => setForm((f) => ({ ...f, preferred_gender: val }))}
                data-testid={`role-gender-${val}`}
                className={`px-4 py-2 text-sm uppercase tracking-wider border transition-colors ${(form.preferred_gender || "either") === val ? "border-blood bg-blood/15 text-[#F2EBE1]" : "border-[#262626] text-[#A19D94] hover:border-[#3a3835]"}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2 pt-2 border-t border-[#1a1a1a] mt-2">
          <label className="label-mini block mb-1">Ideal Ability Profile <span className="text-[#6B6862] normal-case">(for audition match suggestions — set 0-10; leave at 0 if an ability doesn't matter for this role)</span></label>
          <AbilityTargetsEditor targets={form.ability_targets || {}} onChange={(t) => setForm((f) => ({ ...f, ability_targets: t }))} />
        </div>
        <div className="md:col-span-2 pt-2 border-t border-[#1a1a1a] mt-2">
          <label className="label-mini block mb-3">Character Profile Builder <span className="text-[#6B6862] normal-case">(one shared character sheet)</span></label>
          <CharacterProfileBuilder profileFields={form.profile_fields || []} profileValues={form.profile_values || {}} customFields={form.custom_fields || []}
            onChange={({ profileFields, profileValues, customFields }) => setForm((f) => ({ ...f, profile_fields: profileFields, profile_values: profileValues, custom_fields: customFields }))} />
        </div>
      </div>
      <div className="mt-5 flex gap-2 pt-3 border-t border-[#1a1a1a]">
        <button onClick={onSave} data-testid="role-save-btn" className="px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">{editingRepId ? "Save Changes" : "Save"}</button>
        <button onClick={onCancel} data-testid="role-cancel-btn" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#3a3835] hover:text-[#F2EBE1] flex items-center gap-1.5"><X size={13} /> Cancel</button>
      </div>
    </div>
  );
}
